import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, MapPin, ShieldCheck, Sparkles, Users } from "lucide-react";
import api from "../lib/api";
import { BookingSuccessDialog } from "../lib/booking-success";
import { TOUR_FALLBACK_IMAGES, TourBookingDialog, formatDuration, resolvePrice } from "./Tours";
import { formatISK } from "../lib/currency";
import { applySeo } from "../lib/seo";

function paragraphize(text) {
  return String(text || "")
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);
}

function lineList(text) {
  return String(text || "")
    .split("\n")
    .map((chunk) => chunk.trim())
    .filter(Boolean);
}

function normalizeImages(tour) {
  const images = [tour?.featured_image, ...(tour?.gallery_images || [])].filter(Boolean);
  return [...new Set(images)];
}

function packageOptions(tour) {
  const options = [];
  if (tour?.supports_4pack && tour?.price_4pack != null) options.push({ key: "4pack", title: "4-pack", price: tour.price_4pack, copy: "Ideal for up to 4 guests" });
  if (tour?.supports_8pack && tour?.price_8pack != null) options.push({ key: "8pack", title: "8-pack", price: tour.price_8pack, copy: "Ideal for up to 8 guests" });
  return options;
}

function itineraryItems(tour) {
  if (Array.isArray(tour?.itinerary)) return tour.itinerary.filter(Boolean);
  return [];
}

function bulletItems(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function InfoPill({ icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3" data-testid={testId}>
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-sky-700">{icon} {label}</p>
      <p className="mt-2 text-sm font-medium text-slate-900">{value}</p>
    </div>
  );
}

function RichListSection({ title, items, testId }) {
  if (!items?.length) return null;
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid={testId}>
      <h2 className="font-heading text-2xl font-semibold text-slate-900">{title}</h2>
      <ul className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
        {items.map((item, index) => (
          <li key={`${title}-${index}`} className="flex gap-3 rounded-2xl bg-slate-50 px-4 py-3">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/tours/${slug}`)
      .then((response) => {
        if (!alive) return;
        setTour(response.data);
      })
      .catch(() => {
        if (!alive) return;
        setError("We couldn't load this tour right now.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    applySeo({
      title: tour?.title ? `${tour.title} — IceFleet Tours` : "IceFleet Tours — Private Iceland Tours",
      description: tour?.short_description || "Private Iceland tour with full itinerary, highlights, and direct booking from IceFleet.",
      canonicalPath: slug ? `/tours/${slug}` : "/tours",
    });
  }, [slug, tour?.title, tour?.short_description]);

  const images = useMemo(() => normalizeImages(tour || {}), [tour]);
  const packages = useMemo(() => packageOptions(tour || {}), [tour]);
  const duration = useMemo(() => formatDuration(tour?.estimated_display_minutes), [tour]);
  const descriptionBlocks = useMemo(() => paragraphize(tour?.full_description), [tour]);
  const itinerary = useMemo(() => itineraryItems(tour || {}), [tour]);
  const highlights = useMemo(() => bulletItems(tour?.highlights), [tour]);
  const included = useMemo(() => bulletItems(tour?.included), [tour]);
  const excluded = useMemo(() => bulletItems(tour?.excluded), [tour]);
  const pickupNotes = useMemo(() => lineList(tour?.pickup_info), [tour]);
  const goodToKnow = useMemo(() => lineList(tour?.good_to_know), [tour]);

  const fallbackImage = TOUR_FALLBACK_IMAGES[slug] || "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1400&q=75";
  const heroImage = images[0] || fallbackImage;

  return (
    <div className="bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_36%),linear-gradient(180deg,#f8fbff_0%,#ffffff_38%)] pb-24" data-testid="tour-detail-page">
      <section className="mx-auto max-w-7xl px-5 py-8 sm:px-8 lg:px-10">
        <Link to="/tours" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900" data-testid="tour-detail-back-link">
          <ArrowLeft size={16} /> Back to tours
        </Link>

        {loading && <div className="py-20 text-center text-slate-500" data-testid="tour-detail-loading">Loading tour details…</div>}

        {!loading && error && (
          <div className="mt-8 rounded-3xl border border-rose-200 bg-rose-50 p-8 text-rose-800" data-testid="tour-detail-error">
            {error}
          </div>
        )}

        {!loading && !error && tour && (
          <>
            <div className="mt-6 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]" data-testid="tour-detail-hero">
              <div className="space-y-5">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-sky-700" data-testid="tour-detail-eyebrow">Private day tour</p>
                <h1 className="font-heading text-4xl font-semibold text-slate-900 sm:text-5xl" data-testid="tour-detail-title">{tour.title}</h1>
                {tour.short_description && <p className="max-w-3xl text-base text-slate-600 sm:text-lg" data-testid="tour-detail-short-description">{tour.short_description}</p>}

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {duration && <InfoPill icon={<Clock size={14} />} label="Duration" value={duration} testId="tour-detail-duration" />}
                  {tour.max_passengers && <InfoPill icon={<Users size={14} />} label="Capacity" value={`Up to ${tour.max_passengers} guests`} testId="tour-detail-capacity" />}
                  <InfoPill icon={<ShieldCheck size={14} />} label="Service" value="Private chauffeur-driven tour" testId="tour-detail-service" />
                  <InfoPill icon={<MapPin size={14} />} label="Booking" value="Select a package, then continue to booking" testId="tour-detail-booking-info" />
                </div>

                <div className="flex flex-wrap gap-3">
                  <button onClick={() => setShowBooking(true)} className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800" data-testid="tour-detail-book-cta">
                    Continue to booking
                  </button>
                  <Link to="/contact" className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:border-slate-900 hover:text-slate-900" data-testid="tour-detail-contact-link">
                    Ask a question first
                  </Link>
                </div>
              </div>

              <div className="space-y-4">
                <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm" data-testid="tour-detail-hero-image">
                  <img src={heroImage} alt={tour.title} className="aspect-[4/3] w-full object-cover" />
                </div>
                {packages.length > 0 && (
                  <div className="grid gap-3 sm:grid-cols-2" data-testid="tour-detail-package-grid">
                    {packages.map((option) => {
                      const price = resolvePrice(tour, option.key, option.key === "8pack" ? 8 : 4);
                      return (
                        <div key={option.key} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`tour-detail-package-${option.key}`}>
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-700">{option.title}</p>
                          <p className="mt-2 font-heading text-2xl font-semibold text-slate-900">{formatISK(price.total)}</p>
                          <p className="mt-1 text-sm text-slate-600">{option.copy}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-10 grid gap-6 xl:grid-cols-[1fr_0.34fr]" data-testid="tour-detail-content-grid">
              <div className="space-y-6">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="tour-detail-description-section">
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">Tour overview</h2>
                  <div className="mt-4 space-y-4 text-sm leading-7 text-slate-700">
                    {descriptionBlocks.map((paragraph, index) => <p key={`desc-${index}`}>{paragraph}</p>)}
                  </div>
                </section>

                {itinerary.length > 0 && (
                  <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="tour-detail-itinerary-section">
                    <h2 className="font-heading text-2xl font-semibold text-slate-900">Itinerary</h2>
                    <div className="mt-5 space-y-4">
                      {itinerary.map((step, index) => (
                        <div key={`${step.title || step}-${index}`} className="rounded-2xl bg-slate-50 px-5 py-4">
                          <div className="flex gap-4">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">{index + 1}</div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-900">{typeof step === "string" ? step : step.title}</h3>
                              {typeof step !== "string" && step.description && <p className="mt-2 text-sm leading-6 text-slate-600">{step.description}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <RichListSection title="Highlights" items={highlights} testId="tour-detail-highlights-section" />

                {(included.length > 0 || excluded.length > 0) && (
                  <div className="grid gap-6 lg:grid-cols-2">
                    <RichListSection title="What’s included" items={included} testId="tour-detail-included-section" />
                    <RichListSection title="What’s excluded" items={excluded} testId="tour-detail-excluded-section" />
                  </div>
                )}

                {images.length > 1 && (
                  <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="tour-detail-gallery-section">
                    <h2 className="font-heading text-2xl font-semibold text-slate-900">Gallery</h2>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {images.slice(1).map((image, index) => (
                        <div key={`${image}-${index}`} className="overflow-hidden rounded-2xl bg-slate-100">
                          <img src={image} alt={`${tour.title} gallery ${index + 1}`} className="aspect-[4/3] w-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              <aside className="space-y-6">
                {(pickupNotes.length > 0 || goodToKnow.length > 0 || tour.cancellation_info) && (
                  <section className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="tour-detail-practical-section">
                    <h2 className="font-heading text-2xl font-semibold text-slate-900">Good to know</h2>
                    {pickupNotes.length > 0 && (
                      <div className="mt-5">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-700">Pickup & transport</p>
                        <ul className="mt-3 space-y-2 text-sm text-slate-700">
                          {pickupNotes.map((note, index) => <li key={`pickup-${index}`}>{note}</li>)}
                        </ul>
                      </div>
                    )}
                    {goodToKnow.length > 0 && (
                      <div className="mt-5">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-700">Before you go</p>
                        <ul className="mt-3 space-y-2 text-sm text-slate-700">
                          {goodToKnow.map((note, index) => <li key={`know-${index}`}>{note}</li>)}
                        </ul>
                      </div>
                    )}
                    {tour.cancellation_info && (
                      <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-700">Cancellation</p>
                        <p className="mt-2 text-sm leading-6 text-slate-700">{tour.cancellation_info}</p>
                      </div>
                    )}
                  </section>
                )}

                {(tour.admission_name || tour.admission_description) && (
                  <section className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="tour-detail-admission-section">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-700">Included admission</p>
                    <h2 className="mt-2 font-heading text-2xl font-semibold text-slate-900">{tour.admission_name || "Admission included"}</h2>
                    {tour.admission_description && <p className="mt-3 text-sm leading-6 text-slate-700">{tour.admission_description}</p>}
                    {tour.admission_price_per_person_isk != null && (
                      <p className="mt-4 text-sm font-semibold text-slate-900">Value included: {formatISK(tour.admission_price_per_person_isk)} per guest</p>
                    )}
                  </section>
                )}

                <section className="rounded-3xl border border-slate-200 bg-slate-900 p-6 text-white" data-testid="tour-detail-booking-sidebar">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-300">Ready to book?</p>
                  <h2 className="mt-3 font-heading text-3xl font-semibold">Secure your private tour</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-200">Check availability, choose the right package, and continue into the existing IceFleet booking and payment flow.</p>
                  <button onClick={() => setShowBooking(true)} className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100" data-testid="tour-detail-sidebar-book-cta">
                    <Sparkles size={16} className="mr-2" /> Select &amp; book
                  </button>
                </section>
              </aside>
            </div>
          </>
        )}
      </section>

      {showBooking && tour && <TourBookingDialog tour={tour} onClose={() => setShowBooking(false)} onCreated={(booking) => { setShowBooking(false); setCreated(booking); }} />}
      {created && <BookingSuccessDialog booking={created} onClose={() => setCreated(null)} />}
    </div>
  );
}